/**
 * Extract a user-friendly error message from a payment API error.
 *
 * Handles:
 *   - Stripe card declines with decline_code
 *   - Backend error responses with debug info
 *   - Generic axios errors
 *   - Unknown errors
 *
 * Usage:
 *   toast.error(getPaymentErrorMessage(err));
 *   toast.error(getPaymentErrorMessage(res));  // for non-2xx responses
 */

interface PaymentDebug {
  type?: string;
  code?: string;
  decline_code?: string;
  message?: string;
  param?: string;
}

/**
 * Extract error message from a caught error (axios catch block).
 */
export function getPaymentErrorMessage(
  error: any,
  fallback = "Payment failed. Please try again."
): string {
  // Axios error with response data
  const data = error?.response?.data || error?.data || error;
  const debug: PaymentDebug | undefined = data?.debug;

  // Primary message: Stripe's user-facing message or backend error
  const baseMessage =
    data?.error ||
    data?.message ||
    error?.message ||
    fallback;

  // Append decline reason if available (e.g., "insufficient funds")
  if (debug?.decline_code) {
    const reason = debug.decline_code.replace(/_/g, " ");
    // Don't duplicate if the base message already contains the reason
    if (!baseMessage.toLowerCase().includes(reason)) {
      return `${baseMessage} (${reason})`;
    }
  }

  // Append error code for non-card errors (e.g., "processing_error")
  if (!debug?.decline_code && debug?.code && debug.code !== "card_declined") {
    const code = debug.code.replace(/_/g, " ");
    if (!baseMessage.toLowerCase().includes(code)) {
      return `${baseMessage} (${code})`;
    }
  }

  return baseMessage;
}

/**
 * "This club cannot take card payments right now."
 *
 * The backend answers 503 with `errorCode: "PAYMENTS_UNAVAILABLE"` when a
 * club has no card processor connected or has card payments switched off
 * (backend utils/stripeUnavailable.js). That is not "try again" and not "check
 * your email address": trying again cannot work. Pages check for it and say
 * so in plain words.
 *
 * Pass the response BODY (what `res.data` holds for the payments API helpers).
 */
export const PAYMENTS_UNAVAILABLE_CODE = "PAYMENTS_UNAVAILABLE";
export const PAYMENTS_UNAVAILABLE_MESSAGE =
  "This club is not set up to take card payments online right now. Please contact the club.";

export function isPaymentsUnavailable(body: any): boolean {
  return body?.errorCode === PAYMENTS_UNAVAILABLE_CODE;
}

/**
 * A card payment STAFF tried to take that the server did not take, for one of
 * the two reasons that "try again" cannot fix. Every staff payment screen says
 * the same thing for each, through staffCardRefusalMessage below.
 *
 * 1. The card's bank wants the cardholder to approve the payment (the server
 *    answers 200 `{ success: false, requires_action: true }`). Only the
 *    cardholder can do that, in their own browser, so no staff page can finish
 *    it. The server ends the attempt; nothing is charged or recorded. What
 *    works: the family pays from their own account, or staff use another card.
 * 2. The club has no card processor (503 `errorCode: "PAYMENTS_UNAVAILABLE"`).
 *    PAYMENTS_UNAVAILABLE_MESSAGE above is written for a family ("contact the
 *    club"); staff ARE the club, so they are told what they can do instead.
 */
export const CARD_NEEDS_APPROVAL_MESSAGE =
  "This card needs the cardholder to approve the payment with their bank, which cannot be done from this page. " +
  "The card was not charged. Ask the family to pay online from their own account, where their bank can ask them to approve it, " +
  "or use a different card or one already saved on the account.";

export const STAFF_CARDS_UNAVAILABLE_MESSAGE =
  "This club is not set up to take card payments. Record cash or a check instead, or connect a card processor in Settings.";

/**
 * Pass the response BODY (`res.data` for acceptPayment). Returns the message
 * to show staff, or null when the body is neither case and the caller's own
 * handling applies.
 */
export function staffCardRefusalMessage(body: any): string | null {
  if (body?.requires_action) return CARD_NEEDS_APPROVAL_MESSAGE;
  if (isPaymentsUnavailable(body)) return STAFF_CARDS_UNAVAILABLE_MESSAGE;
  return null;
}
